/**
 * NoiseModule — owns the three shared noise buffers (white/pink/brown)
 * and the two user-facing parameters (Color, Level).
 *
 * Per-voice wiring lives in Voice: each voice creates its own
 * AudioBufferSourceNode pointed at NoiseModule's shared buffer and runs
 * it through its own noiseGain into the voice's filter, in parallel with
 * the oscillator. That means noise rides the same filter + envelope +
 * VCA chain as the tone — it's gated by every noteOn/noteOff, shaped by
 * the attack/decay/release, and shoved through whatever the filter is
 * doing. Users expect "turn down Level to silence it when no notes" and
 * "hear a thud-plus-hiss attack" — both of those require per-voice.
 *
 * Level is bound to every voice's noiseGain via the standard
 * Parameter.bindAudioParam multi-target feature, so one knob drives all
 * voices uniformly.
 */

import type { AudioEngine } from '../AudioEngine';
import {
  EnumParameter,
  NumericParameter,
  type AnyParameter,
} from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export type NoiseColor = 'white' | 'pink' | 'brown';
const NOISE_COLORS: readonly NoiseColor[] = ['white', 'pink', 'brown'];

/** Length in seconds of each noise buffer. Long enough that the loop
 *  point is imperceptible, short enough to keep memory tiny. */
const BUFFER_SEC = 2;

export class NoiseModule implements SynthModule {
  // 'modulation' kind — this module has no audio I/O at the module level.
  // It exposes buffers + parameters; Voice owns the actual playback.
  readonly kind = 'modulation' as const;
  readonly type = 'noise' as const;
  readonly displayName = 'NOISE';
  readonly id: string;
  bypass = false;

  readonly input: AudioNode | null = null;
  readonly output: AudioNode | null = null;

  readonly color = new EnumParameter<NoiseColor>({
    name: 'Color',
    default: 'white',
    options: NOISE_COLORS,
    formatter: (v) =>
      ({ white: 'WHT', pink: 'PNK', brown: 'BRN' })[v],
  });

  readonly level = new NumericParameter({
    name: 'Level',
    default: 0,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.3, // noise drowns patches fast — default to silent-ish rolls
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.color, this.level];

  private buffers = new Map<NoiseColor, AudioBuffer>();
  private started = false;

  constructor(id?: string) {
    this.id = id ?? mintId('noise');
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    const ctx = engine.context;
    // Pre-bake one buffer per color so color swaps in voices are just a
    // BufferSource reconnection, not a per-voice render pass.
    for (const c of NOISE_COLORS) {
      this.buffers.set(c, generateNoiseBuffer(ctx, c));
    }
    this.started = true;
  }

  dispose(): void {
    this.buffers.clear();
    this.started = false;
  }

  /** Shared buffer for the requested color. Voices call this at init and
   *  again when Color changes. Null if init hasn't happened yet. */
  getBuffer(color: NoiseColor): AudioBuffer | null {
    return this.buffers.get(color) ?? null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }
}

// ── Noise generators ────────────────────────────────────────────────────

function generateNoiseBuffer(ctx: BaseAudioContext, color: NoiseColor): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * BUFFER_SEC);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  switch (color) {
    case 'white':
      for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
      break;
    case 'pink':
      // Voss-McCartney approximation. Seven-pole IIR — cheap and sounds
      // close enough to true pink noise for synth duty.
      {
        let b0 = 0,
          b1 = 0,
          b2 = 0,
          b3 = 0,
          b4 = 0,
          b5 = 0,
          b6 = 0;
        for (let i = 0; i < length; i++) {
          const white = Math.random() * 2 - 1;
          b0 = 0.99886 * b0 + white * 0.0555179;
          b1 = 0.99332 * b1 + white * 0.0750759;
          b2 = 0.969 * b2 + white * 0.153852;
          b3 = 0.8665 * b3 + white * 0.3104856;
          b4 = 0.55 * b4 + white * 0.5329522;
          b5 = -0.7616 * b5 - white * 0.016898;
          data[i] =
            (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
          b6 = white * 0.115926;
        }
      }
      break;
    case 'brown':
      // Integrated white noise with a leaky integrator so values don't
      // drift out of range. Sounds deep — think distant thunder.
      {
        let last = 0;
        for (let i = 0; i < length; i++) {
          const white = Math.random() * 2 - 1;
          last = (last + 0.02 * white) / 1.02;
          data[i] = Math.max(-1, Math.min(1, last * 3.5));
        }
      }
      break;
  }
  return buffer;
}
